import {
    Box,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Link,
    Paper,
    Stack,
    Tab,
    TablePagination,
    Tabs,
    Typography,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScreenBanner from "../../../../../components/ScreenBanner";
import LabelPrinter from "../../../../../components/LabelPrinter";
import { formatarValorMonetario } from "../../../../../helpers/masks";
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import { IDataProcessos } from "../../../../../types/api/bpm/pesquisar-processos";
import IRequest from "../../../../../types/IRequest";
import TagsSituacao from "../../../../../components/TagsSituacao";
import { TTagsSituacao } from "../../../../../types/ITagSituacao";
import moment from "moment";
import { IDataContadorSolicitacoesAnalCred } from "../../../../../types/api/financeiro/contar-solicitacoes-analise-credito";
import { useSearch } from "../../../../../contexts/SearchContext";
import HighlightedText from "../../../../../components/HighlightSearch";
import { FiltroModal } from "./FiltrosDialog";
import { useModalDetalheProcesso } from "../../../../../components/ModalContexts/ModalDetalheProcessoContext";
import SearchWithFilters from "../../../../../components/BasicComponents/SearchWithFilters";
import useDebounce from "../../../../../lib/useDebounce";
import ChipList from "../../../../../components/BasicComponents/ChipList";
import { getChipFilterItems } from "../../../../../helpers/getChipFilterItems";

export default function PageAnaliseCredito() {

    const { setSearchTerm } = useSearch();

    const [tab, setTab] = useState<number>(0);
    const [palavraChave, setPalavraChave] = useState("");
    const [openFilterModal, setOpenFilterModal] = useState(false);
    const [filtros, setFiltros] = useState<IFilterState>(defaultFilterState);

    const deboucedSearchTerm = useDebounce(palavraChave);

    const paramsQuery = {
        tarefa_atual: filtros.tarefa_atual,
        ...(TAB_IDS[tab] !== "Todas" ? { situacao: [TAB_IDS[tab]] } : {}),
    };

    //queries
    const { data: preDataContadores, isLoading: isLoadingContadores } = useQuery({
        queryKey: ["contar-solicitacoes-analise-credito", filtros.tarefa_atual],
        queryFn: () => axiosClient.get("financeiro/analise-credito/contar-solicitacoes-analise-credito", {
            params: {
                tarefa_atual: filtros.tarefa_atual,
            },
        }),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
    const dataContadores: IDataContadorSolicitacoesAnalCred = useMemo(() => preDataContadores?.data, [preDataContadores])

    const { data, isLoading: isLoadingProcessos } = useQuery({
        queryKey: ["pesquisar-solicitacoes-analise-credito", filtros, tab, deboucedSearchTerm],
        queryFn: () =>
            axiosClient.get("financeiro/analise-credito/pesquisar-solicitacoes-analise-credito", {
                params: {
                    pesquisar: deboucedSearchTerm,
                    limit: filtros.porPagina,
                    page: filtros.pagina + 1,
                    ...paramsQuery,
                },
            }),
        staleTime: 0,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    });
    const { reqProcessos, dataProcessos }: IProcessos = useMemo(
        () => ({
            reqProcessos: data?.data,
            dataProcessos: data?.data.data || [],
        }),
        [data]
    );
    //

    //funcoes manipuladoras
    const handleChangePage = useCallback(
        (_: React.MouseEvent<HTMLButtonElement> | null, novaPag: number) => {
            setFiltros((prevState) => ({
                ...prevState,
                pagina: novaPag,
            }));
        },
        []
    );

    const handleChangeRowsPerPage = useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setFiltros((prevState) => ({
                ...prevState,
                porPagina: parseInt(event.target.value, 10),
                pagina: 0,
            }));
        },
        []
    );
    const handleFilterSubmit = (form: IFilterState) => {
        setFiltros(form);
    };

    const handleSearch = (term: string) => {
        setSearchTerm(term);
        setFiltros((prevState) => ({
            ...prevState,
            pagina: 0
        }));
    }

    const handleChangeTabs = (_: React.SyntheticEvent, newValue: number) => {
        setFiltros((prevState) => ({
            ...prevState,
            pagina: 0,
            porPagina: 10,
        }));
        setTab(newValue);
    }

    const handleDeleteFilter = (item: string) => {
        const newFiltros = { ...filtros };

        Object.entries(filtros).forEach(([key, value]) => {
            const label = filtersChipLabels[key as keyof typeof filtersChipLabels] || key;
            const displayValue = Array.isArray(value) ? value.join(', ') : value;
            const chipText = `${label}: ${displayValue}`;

            if (chipText === item) {
                delete newFiltros[key as keyof IFilterState];
            }
        });

        setFiltros(newFiltros);
    }
    //

    const chipItems = useMemo(() => {
        return getChipFilterItems(
            filtros,
            filtersChipLabels,
            ['pagina', 'porPagina']
        );
    }, [filtros]);

    return (
        <>
            <ScreenBanner color="#6EB834" title="Financeiro" />

            <Container sx={{ mt: 5 }}>
                <Typography textAlign="center" variant="h5" mb={3}>
                    Solicitações de análise de crédito
                </Typography>
                <Grid container spacing={3} justifyContent="center">
                    <Grid size={{ xs: 12 }}>
                        <Box component={Paper} p={2}>
                            <SearchWithFilters
                                palavraChave={palavraChave}
                                textFieldProps={{
                                    label: "Pesquisar",
                                    placeholder: "Pesquise por título, código ou descrição de um processo",
                                    size: "medium",
                                }}
                                pesquisar={handleSearch}
                                setTermoPesquisa={setPalavraChave}
                                handleOpenFilters={setOpenFilterModal}
                            />
                            <ChipList
                                items={chipItems}
                                onDelete={handleDeleteFilter}
                            />

                            {dataContadores?.tarefas && dataContadores.tarefas.length > 0 && (
                                <Accordion
                                    sx={{
                                        boxShadow: 'none',
                                        '&:before': { display: 'none' },
                                        border: 'none',
                                        backgroundColor: 'transparent',
                                    }}
                                    disableGutters
                                >
                                    <AccordionSummary
                                        expandIcon={<Tooltip title="Expandir tarefas"><ExpandMoreIcon /></Tooltip>}
                                        aria-controls="tarefas-content"
                                        id="tarefas-header"
                                        sx={{
                                            px: 0,
                                            minHeight: 0,
                                            height: 0,
                                            '&.Mui-expanded': { minHeight: 30 }
                                        }}
                                    >
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 0 }}>
                                        <Grid container spacing={2}>
                                            {dataContadores.tarefas.map((tarefa, index) => (
                                                <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                                    <Card
                                                        variant="outlined"
                                                        sx={{
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease',
                                                            '&:hover': {
                                                                boxShadow: 2,
                                                                transform: 'translateY(-2px)',
                                                            }
                                                        }}
                                                        onClick={() => {
                                                            setFiltros((prevState) => ({
                                                                ...prevState,
                                                                tarefa_atual: tarefa.titulo,
                                                                pagina: 0,
                                                            }));
                                                            setTab(0);
                                                        }}
                                                    >
                                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                            <Typography
                                                                variant="h4"
                                                                color="primary"
                                                                fontWeight="bold"
                                                            >
                                                                {tarefa.quantidade}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                {tarefa.titulo}
                                                            </Typography>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            <Tabs
                                variant="scrollable"
                                scrollButtons="auto"
                                value={tab}
                                onChange={handleChangeTabs}
                            >
                                <Tab label={`Todas (${dataContadores?.total || 0})`} id={TAB_IDS[0]} />
                                <Tab label={`Em execução (${dataContadores?.total_abertos || 0})`} id={TAB_IDS[1]} />
                                <Tab label={`Finalizadas (${dataContadores?.total_finalizados || 0})`} id={TAB_IDS[2]} />
                                <Tab label={`Suspensas (${dataContadores?.total_suspensos || 0})`} id={TAB_IDS[3]} />
                            </Tabs>
                            {isLoadingProcessos && (
                                <Box display="flex" justifyContent="center" my={4}>
                                    <CircularProgress />
                                </Box>
                            )}


                            {!isLoadingProcessos && (
                                <Box>
                                    {!dataProcessos.length && (
                                        <Box display="flex" justifyContent="center" my={4}>
                                            <Typography variant="subtitle2" color="textSecondary" >
                                                Nenhum processo foi encontrado
                                            </Typography>
                                        </Box>
                                    )}
                                    {dataProcessos.map((item, index) => {
                                        let nome = "-";

                                        if (item.extras?.nome && item.extras?.tipo_publico && item.extras?.codigo_publico) {
                                            nome = item.extras.tipo_publico + item.extras.codigo_publico + ' - ' + item.extras.nome
                                        }
                                        return (
                                            <ItemListagem
                                                key={index}
                                                codigo={item.codigo}
                                                status={item.situacao}
                                                solicitante={item.usuario}
                                                criadoEm={item.created_at}
                                                nome={nome}
                                                prazo={item.extras?.prazo_solicitacao || "-"}
                                                valorSolicitado={item.extras?.valor_solicitacao || 0}
                                                tarefaAtual={item.tarefa_atual.titulo}
                                            />
                                        )
                                    })}
                                </Box>
                            )
                            }

                            <TablePagination
                                component="div"
                                count={reqProcessos?.total ?? 0}
                                page={filtros.pagina}
                                onPageChange={handleChangePage}
                                rowsPerPage={filtros.porPagina ?? 0}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage="Linhas por página"
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {openFilterModal && (
                <FiltroModal
                    {...{ filtros }}
                    open={openFilterModal}
                    handleClose={() => setOpenFilterModal(false)}
                    handleFilterSubmit={(form) => {
                        handleFilterSubmit(form)
                    }}
                    onClearFilters={() => {
                        setFiltros(defaultFilterState)
                        setSearchTerm('')
                    }}
                    tarefasOptions={dataContadores?.tarefas.map(i => i.titulo) || []}
                />
            )}
        </>
    );
}

function ItemListagem({
    nome,
    codigo,
    criadoEm,
    status,
    prazo,
    valorSolicitado,
    tarefaAtual,
    solicitante,
}: ItemListagemProps) {
    const { openModal } = useModalDetalheProcesso();

    const dataCriacao = useMemo(
        () => moment(criadoEm).format("DD/MM/YYYY HH:mm"),
        [criadoEm]
    );

    //funcao manipuladora
    const handleOpenModalDetail = useCallback((codigo: string) => {
        openModal(codigo);
    }, [openModal]);
    //

    return (
        <Box width="100%" borderBottom="1px solid #ccc" p={2}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Link href="#" onClick={() => handleOpenModalDetail(codigo)}>
                        <HighlightedText text={`#${codigo}`} />
                    </Link>
                    <Typography variant="subtitle2">{dataCriacao}</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                    <LabelPrinter titulo="Público" valor={nome} size="small" />
                    <Typography variant="caption"><HighlightedText text={`Solicitante: ${solicitante}`} /></Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                    <LabelPrinter
                        titulo="Valor solicitado"
                        valor={formatarValorMonetario(valorSolicitado)}
                        size="small"
                    />
                    <Typography variant="caption">Prazo: {prazo}</Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TagsSituacao situacao={status} filled />
                        {tarefaAtual && (
                            <Chip size="small" label={tarefaAtual} color="warning" variant="outlined" />
                        )}
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}

const TAB_IDS = ["Todas", "Em execução", "Finalizado", "Suspenso"];

const defaultFilterState = {
    situacoes: null,
    tarefa_atual: "",
    pagina: 0,
    porPagina: 10,
};
interface ItemListagemProps {
    nome: string;
    codigo: string;
    criadoEm: string;
    status: TTagsSituacao;
    tarefaAtual: string | null;
    valorSolicitado: number;
    prazo: string;
    solicitante: string;
}
interface IProcessos {
    reqProcessos: IRequest<IDataProcessos>;
    dataProcessos: IDataProcessos[];
}
export interface IFilterState {
    tarefa_atual: string
    pagina: number;
    porPagina: number;
}

const filtersChipLabels: { [key in keyof Omit<IFilterState, 'pagina' | 'porPagina'>]: string } = {
    tarefa_atual: "Tarefa atual",
};